import { useMemo } from 'react';
import {
  useMeetings,
  useQualifiedLeads,
  usePreQualifiedLeads,
} from './useSupabaseLeads';
import { usePipelineDeals } from './usePipelineDeals';
import { useDashboardCounts } from './useDashboardCounts';

interface AgentStats {
  totalSent: number;
  replies: number;
  engaged: number;
  booked: number;
  conversionRate: number;
}

interface CallAgentStats {
  totalCalls: number;
  connected: number;
  engaged: number;
  booked: number;
  conversionRate: number;
}

interface LeadGenStats {
  preQualified: number;
  qualified: number;
  conversionRate: number;
}

interface TopMetrics {
  qualifiedLeads: number;
  leadsContacted: number;
  callsDone: number;
  whatsappsSent: number;
  emailsSent: number;
  linkedInSent: number;
  meetingsBooked: number;
  pipelineGenerated: number;
}

interface RecentLead {
  id: string;
  dm_full_name: string;
  company_name: string;
  lead_status: string;
  last_reply_timestamp: string;
  channel: string;
}

export interface DashboardStats {
  metrics: TopMetrics;
  leadGenStats: LeadGenStats;
  callAgentStats: CallAgentStats;
  whatsappAgentStats: AgentStats;
  emailAgentStats: AgentStats;
  linkedInAgentStats: AgentStats;
  recentLeads: RecentLead[];
  isLoading: boolean;
}

export function useDashboardStats(): DashboardStats {
  const { data: meetingsData = [], isLoading: l1 } = useMeetings();
  const { data: qualifiedData = [], isLoading: l2 } = useQualifiedLeads();
  const { data: preQualifiedData = [], isLoading: l7 } = usePreQualifiedLeads();
  const { data: dealsData = [], isLoading: l8 } = usePipelineDeals();
  const {
    callsDone,
    whatsappsSent,
    emailsSent,
    linkedInMessagesSent,
    connectedCalls,
    whatsappReplies,
    emailReplies,
    linkedInReplies,
    recentCallLogs,
    isLoading: l9,
  } = useDashboardCounts();

  const isLoading = l1 || l2 || l7 || l8 || l9;

  const metrics = useMemo<TopMetrics>(() => {
    const liCrSentCount = qualifiedData.filter(
      (l: any) => l.la_cr_sent === true
    ).length;
    const linkedInSent = linkedInMessagesSent + liCrSentCount;

    const leadsContacted = qualifiedData.filter((lead: any) =>
      lead.sent_to_ca || lead.sent_to_wa || lead.sent_to_ea || lead.sent_to_la
    ).length;

    const pipelineGenerated = dealsData.reduce(
      (sum: number, d: any) => sum + (d.deal_value || 0),
      0
    );

    return {
      qualifiedLeads: qualifiedData.length,
      leadsContacted,
      callsDone,
      whatsappsSent,
      emailsSent,
      linkedInSent,
      meetingsBooked: meetingsData.length,
      pipelineGenerated,
    };
  }, [meetingsData, qualifiedData, callsDone, whatsappsSent, emailsSent, linkedInMessagesSent, dealsData]);

  const leadGenStats = useMemo<LeadGenStats>(() => {
    const preQ = preQualifiedData.length;
    const q = qualifiedData.length;
    return {
      preQualified: preQ,
      qualified: q,
      conversionRate: preQ > 0 ? Math.round((q / preQ) * 100) : 0,
    };
  }, [preQualifiedData, qualifiedData]);

  const callAgentStats = useMemo<CallAgentStats>(() => {
    const totalCalls = qualifiedData.reduce(
      (sum: number, l: any) => sum + (parseInt(l.ca_call_attempts) || 0),
      0
    );
    const connected = connectedCalls;
    const engaged = qualifiedData.filter((l: any) => {
      const t = (l.ca_lead_temp || '').toLowerCase();
      return t.includes('hot') || t.includes('warm');
    }).length;
    const booked = qualifiedData.filter(
      (l: any) => (l.ca_lead_status || '').toLowerCase() === 'booked'
    ).length;
    const contacted = qualifiedData.filter(
      (l: any) => (parseInt(l.ca_call_attempts) || 0) > 0
    ).length;
    return {
      totalCalls,
      connected,
      engaged,
      booked,
      conversionRate: contacted > 0 ? Math.round((booked / contacted) * 100) : 0,
    };
  }, [qualifiedData, connectedCalls]);

  const whatsappAgentStats = useMemo<AgentStats>(() => {
    const totalSent = qualifiedData.reduce(
      (sum: number, l: any) => sum + (parseInt(l.wa_messages_sent) || 0),
      0
    );
    const replies = whatsappReplies;
    const engaged = qualifiedData.filter((l: any) => {
      const t = (l.wa_lead_temp || '').toLowerCase();
      return t.includes('hot') || t.includes('warm');
    }).length;
    const booked = qualifiedData.filter(
      (l: any) => (l.wa_lead_status || '').toLowerCase() === 'booked'
    ).length;
    const contacted = qualifiedData.filter(
      (l: any) => (parseInt(l.wa_messages_sent) || 0) > 0
    ).length;
    return {
      totalSent,
      replies,
      engaged,
      booked,
      conversionRate: contacted > 0 ? Math.round((booked / contacted) * 100) : 0,
    };
  }, [qualifiedData, whatsappReplies]);

  const emailAgentStats = useMemo<AgentStats>(() => {
    const totalSent = qualifiedData.reduce(
      (sum: number, l: any) => sum + (parseInt(l.ea_emails_sent) || 0),
      0
    );
    const replies = emailReplies;
    const engaged = qualifiedData.filter((l: any) => {
      const t = (l.ea_lead_temp || '').toLowerCase();
      return t.includes('hot') || t.includes('warm');
    }).length;
    const booked = qualifiedData.filter(
      (l: any) => (l.ea_lead_status || '').toLowerCase() === 'booked'
    ).length;
    const contacted = qualifiedData.filter(
      (l: any) => (parseInt(l.ea_emails_sent) || 0) > 0
    ).length;
    return {
      totalSent,
      replies,
      engaged,
      booked,
      conversionRate: contacted > 0 ? Math.round((booked / contacted) * 100) : 0,
    };
  }, [qualifiedData, emailReplies]);

  const linkedInAgentStats = useMemo<AgentStats>(() => {
    const totalSent = qualifiedData.reduce(
      (sum: number, l: any) => sum + (parseInt(l.la_messages_sent) || 0),
      0
    );
    const replies = linkedInReplies;
    const engaged = qualifiedData.filter((l: any) => {
      const t = (l.la_lead_temp || '').toLowerCase();
      return t.includes('hot') || t.includes('warm');
    }).length;
    const booked = qualifiedData.filter(
      (l: any) => (l.la_lead_status || '').toLowerCase() === 'booked'
    ).length;
    const contacted = qualifiedData.filter(
      (l: any) => (parseInt(l.la_messages_sent) || 0) > 0
    ).length;
    return {
      totalSent,
      replies,
      engaged,
      booked,
      conversionRate: contacted > 0 ? Math.round((booked / contacted) * 100) : 0,
    };
  }, [qualifiedData, linkedInReplies]);

  const recentLeads = useMemo<RecentLead[]>(() => {
    const results: RecentLead[] = [];

    for (const log of recentCallLogs.slice(0, 2)) {
      const matchingLead = qualifiedData.find((l: any) => l.lead_id === log.lead_id);
      results.push({
        id: log.call_id_internal || log.lead_id,
        dm_full_name: matchingLead?.dm_full_name || 'Unknown',
        company_name: matchingLead?.company_name || 'Unknown Company',
        lead_status: log.call_outcome || 'Called',
        last_reply_timestamp: log.created_at,
        channel: 'Call',
      });
    }

    const waRecent = qualifiedData
      .filter((l: any) => (parseInt(l.wa_replies_received) || 0) > 0 && l.wa_last_reply_timestamp)
      .sort((a: any, b: any) => new Date(b.wa_last_reply_timestamp).getTime() - new Date(a.wa_last_reply_timestamp).getTime())
      .slice(0, 2);

    for (const lead of waRecent) {
      results.push({
        id: `wa-${lead.lead_id}`,
        dm_full_name: lead.dm_full_name || 'Unknown',
        company_name: lead.company_name || 'Unknown Company',
        lead_status: lead.wa_lead_status || 'Replied',
        last_reply_timestamp: lead.wa_last_reply_timestamp,
        channel: 'WhatsApp',
      });
    }

    const emailRecent = qualifiedData
      .filter((l: any) => (parseInt(l.ea_replies_received) || 0) > 0 && l.ea_last_reply_timestamp)
      .sort((a: any, b: any) => new Date(b.ea_last_reply_timestamp).getTime() - new Date(a.ea_last_reply_timestamp).getTime())
      .slice(0, 2);

    for (const lead of emailRecent) {
      results.push({
        id: `ea-${lead.lead_id}`,
        dm_full_name: lead.dm_full_name || 'Unknown',
        company_name: lead.company_name || 'Unknown Company',
        lead_status: lead.ea_lead_status || 'Replied',
        last_reply_timestamp: lead.ea_last_reply_timestamp,
        channel: 'Email',
      });
    }

    const liRecent = qualifiedData
      .filter((l: any) => (parseInt(l.la_replies_received) || 0) > 0 && l.la_last_reply_timestamp)
      .sort((a: any, b: any) => new Date(b.la_last_reply_timestamp).getTime() - new Date(a.la_last_reply_timestamp).getTime())
      .slice(0, 2);

    for (const lead of liRecent) {
      results.push({
        id: `la-${lead.lead_id}`,
        dm_full_name: lead.dm_full_name || 'Unknown',
        company_name: lead.company_name || 'Unknown Company',
        lead_status: lead.la_lead_status || 'Replied',
        last_reply_timestamp: lead.la_last_reply_timestamp,
        channel: 'LinkedIn',
      });
    }

    return results
      .sort((a, b) => new Date(b.last_reply_timestamp || 0).getTime() - new Date(a.last_reply_timestamp || 0).getTime())
      .slice(0, 8);
  }, [recentCallLogs, qualifiedData]);

  return {
    metrics,
    leadGenStats,
    callAgentStats,
    whatsappAgentStats,
    emailAgentStats,
    linkedInAgentStats,
    recentLeads,
    isLoading,
  };
}
